import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ListingImage } from '@/components/common/ListingImage';
import { formatPrice, formatNumber, formatDaysOnMarketShort } from '@/utils/format';
import { PropertyTypeIcon } from './PropertyTypeIcon';
import { FavoriteButton } from '@/components/common/FavoriteButton';

const TYPE_ORDER = {
  'Single Family': 0, 'Condo': 1, 'Townhouse': 2, 'Multi Family': 3,
  'Land': 4, 'Mobile': 5, 'Other': 6,
};

function sortValue(l, key) {
  switch (key) {
    case 'type': return TYPE_ORDER[l.type] ?? 99;
    case 'price': return l.price ?? null;
    case 'pricePerSqft': return l.pricePerSqft ?? null;
    case 'beds': return l.beds ?? null;
    case 'baths': return l.baths ?? null;
    case 'sqft': return l.sqft ?? null;
    case 'lotSize': return l.lotSize ?? null;
    case 'daysOnMarket': return l.daysOnMarket ?? null;
    default: return null;
  }
}

function sortItems(items, sortKey, sortDir) {
  if (!sortKey) return items;
  return [...items].sort((a, b) => {
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });
}

const SortTH = ({ label, sortKey, activeKey, activeDir, onSort }) => {
  const isActive = activeKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-800 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          <span className="text-blue-600">{activeDir === 'asc' ? '▲' : '▼'}</span>
        ) : (
          <span className="text-slate-300">⇅</span>
        )}
      </span>
    </th>
  );
};

const TH = ({ children }) => (
  <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
    {children}
  </th>
);

export const CompareTable = ({ items, selectedId, onSelect }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => sortItems(items, sortKey, sortDir), [items, sortKey, sortDir]);

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white border-b border-slate-200">
          <tr>
            <TH>Photo</TH>
            <SortTH label="Type" sortKey="type" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="Price" sortKey="price" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="$/Sqft" sortKey="pricePerSqft" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="Bd" sortKey="beds" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="Ba" sortKey="baths" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="Sqft" sortKey="sqft" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="Lot Size" sortKey="lotSize" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <SortTH label="On Site" sortKey="daysOnMarket" activeKey={sortKey} activeDir={sortDir} onSort={handleSort} />
            <TH></TH>
          </tr>
        </thead>
        <tbody>
          {sorted.map((l) => {
            const isSelected = l.id === selectedId;
            const photos = l.photos?.length ? l.photos : ['/placeholder.jpg'];
            return (
              <tr
                key={l.id}
                onClick={() => onSelect(l.id)}
                aria-selected={isSelected}
                className={cn(
                  'cursor-pointer border-b border-slate-100 last:border-0 transition-colors',
                  isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                )}
              >
                <td className="px-3 py-2">
                  <ListingImage
                    src={photos[0]}
                    alt={`${l.type} at ${l.address}`}
                    className="h-10 w-14 rounded object-cover"
                  />
                </td>
                <td className="px-3 py-2 text-slate-500">
                  <PropertyTypeIcon type={l.type} className="h-4 w-4" />
                </td>
                <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">
                  {formatPrice(l.price, l.isRental)}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {l.pricePerSqft ? `$${formatNumber(l.pricePerSqft)}` : '—'}
                </td>
                <td className="px-3 py-2 text-slate-600">{l.beds ?? '—'}</td>
                <td className="px-3 py-2 text-slate-600">{l.baths ?? '—'}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {l.sqft ? formatNumber(l.sqft) : '—'}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {l.lotSize ? `${formatNumber(l.lotSize)} Sqft` : '—'}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {formatDaysOnMarketShort(l.daysOnMarket)}
                </td>
                <td
                  className="px-3 py-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FavoriteButton id={l.id} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
